import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, UserPlus, AlertCircle, ChevronDown, ChevronRight, Building2, LayoutList, Tag, Network } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { crmApi, ApiError } from '@solara/api-client';
import type { LeadDto } from '@solara/api-client';

const STATUS_TONES: Record<LeadDto['status'], 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  NEW: 'info',
  CONTACTED: 'warning',
  QUALIFIED: 'success',
  DISQUALIFIED: 'danger',
  CONVERTED: 'neutral',
};

type GroupMode = 'none' | 'industry' | 'parent' | 'accountTier';

export default function LeadsPage() {
  const { t } = useTranslation('p1');
  const [leads, setLeads] = useState<LeadDto[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<LeadDto['status'] | 'all'>('all');
  const [groupMode, setGroupMode] = useState<GroupMode>('industry');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [status]);

  async function load() {
    try {
      setLeads(await crmApi.listLeads(status === 'all' ? undefined : { status }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  const filtered = useMemo(() => {
    if (!search) return leads;
    const needle = search.toLowerCase();
    return leads.filter((l) =>
      [l.company?.companyName, l.company?.primaryContactEmail, l.company?.trn]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(needle)),
    );
  }, [leads, search]);

  const groups = useMemo(() => {
    if (groupMode === 'none') return [{ key: 'all', label: t('crm.allLeads', { defaultValue: 'All leads' }), leads: filtered }];
    const map = new Map<string, LeadDto[]>();
    for (const l of filtered) {
      let key: string;
      if (groupMode === 'industry') {
        key = l.company?.industry ?? 'OTHER';
      } else if (groupMode === 'parent') {
        key =
          l.company?.parentCompany?.companyName ??
          l.company?.parentCompanyId ??
          t('crm.standaloneCompanies', { defaultValue: 'Standalone' });
      } else {
        key = l.company?.accountTier ?? t('crm.untieredAccounts', { defaultValue: 'Untiered' });
      }
      const arr = map.get(key) ?? [];
      arr.push(l);
      map.set(key, arr);
    }
    return Array.from(map.entries())
      .map(([key, ls]) => ({ key, label: key, leads: ls }))
      .sort((a, b) => b.leads.length - a.leads.length);
  }, [filtered, groupMode, t]);

  function toggle(key: string) {
    setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('crm.leadsTitle')}</h1>
          <p className="text-fg-muted text-sm">{t('crm.leadsSubtitle')}</p>
        </div>
        <Link to="/crm/leads/new">
          <Button>
            <UserPlus className="mr-2 h-4 w-4" />
            {t('crm.newLead')}
          </Button>
        </Link>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <Search className="absolute start-2 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('crm.searchLeads')}
            className="ps-8"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as LeadDto['status'] | 'all')}
          className="rounded border border-border bg-surface px-3 py-2 text-sm"
        >
          <option value="all">{t('crm.allStatuses')}</option>
          {(['NEW', 'CONTACTED', 'QUALIFIED', 'DISQUALIFIED', 'CONVERTED'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`crm.leadStatuses.${s}`, { defaultValue: s })}
            </option>
          ))}
        </select>
        <div className="ms-auto flex items-center gap-1 rounded border border-border bg-surface px-1 py-1 text-xs">
          <GroupBtn current={groupMode} mode="none" onClick={setGroupMode} icon={<LayoutList className="h-3 w-3" />}>
            {t('crm.groupNone', { defaultValue: 'Flat' })}
          </GroupBtn>
          <GroupBtn current={groupMode} mode="industry" onClick={setGroupMode} icon={<Tag className="h-3 w-3" />}>
            {t('crm.groupByIndustry', { defaultValue: 'Industry' })}
          </GroupBtn>
          <GroupBtn current={groupMode} mode="parent" onClick={setGroupMode} icon={<Network className="h-3 w-3" />}>
            {t('crm.groupByParent', { defaultValue: 'Group' })}
          </GroupBtn>
          <GroupBtn current={groupMode} mode="accountTier" onClick={setGroupMode} icon={<Building2 className="h-3 w-3" />}>
            {t('crm.groupByTier', { defaultValue: 'Tier' })}
          </GroupBtn>
        </div>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('crm.leadsCount', { count: filtered.length })}</CardTitle>
        </CardHeader>
        <CardBody>
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('crm.noLeadsFound')}</p>
          ) : (
            <div className="space-y-4">
              {groups.map((g) => {
                const isCollapsed = collapsed[g.key] === true;
                return (
                  <section key={g.key}>
                    {groupMode !== 'none' && (
                      <button
                        onClick={() => toggle(g.key)}
                        className="flex w-full items-center justify-between rounded px-2 py-1.5 text-start text-xs uppercase tracking-wide text-fg-muted hover:bg-surface-muted"
                      >
                        <span className="flex items-center gap-2">
                          {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {g.label}
                        </span>
                        <Badge tone="neutral" className="text-[10px]">
                          {g.leads.length}
                        </Badge>
                      </button>
                    )}
                    {!isCollapsed && (
                      <ul className="divide-y divide-border">
                        {g.leads.map((lead) => (
                          <li key={lead.id} className="flex items-center justify-between py-3 text-sm">
                            <div className="min-w-0">
                              <p className="font-medium">
                                <Link to={`/crm/leads/${lead.id}`} className="hover:underline">
                                  {lead.company?.companyName ?? t('crm.unknownCompany')}
                                </Link>
                              </p>
                              <p className="text-xs text-fg-muted">
                                {lead.company?.primaryContactFirstName} {lead.company?.primaryContactLastName}
                                {' · '}
                                {lead.company?.primaryContactEmail}
                                {lead.duplicateSimilarityScore != null && lead.duplicateSimilarityScore > 70 && (
                                  <span className="ms-2 text-warning">
                                    {t('crm.possibleDuplicate', { score: lead.duplicateSimilarityScore })}
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {typeof lead.leadScore === 'number' && lead.leadScore > 0 && (
                                <Badge tone={lead.leadScore >= 70 ? 'success' : lead.leadScore >= 40 ? 'warning' : 'neutral'} className="text-[10px]">
                                  {t('crm.leadScoreShort', { defaultValue: 'Score' })}: {lead.leadScore}
                                </Badge>
                              )}
                              {lead.company?.accountTier && (
                                <Badge tone="info" className="text-[10px]">
                                  {lead.company.accountTier}
                                </Badge>
                              )}
                              <Badge tone={STATUS_TONES[lead.status]} className="text-[10px]">
                                {t(`crm.leadStatuses.${lead.status}`, { defaultValue: lead.status })}
                              </Badge>
                              <Badge tone="neutral" className="text-[10px]">
                                {t('crm.source')}: {lead.leadSource}
                              </Badge>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function GroupBtn({
  current,
  mode,
  onClick,
  icon,
  children,
}: {
  current: GroupMode;
  mode: GroupMode;
  onClick: (m: GroupMode) => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  const active = current === mode;
  return (
    <button
      type="button"
      onClick={() => onClick(mode)}
      className={`inline-flex items-center gap-1 rounded px-2 py-1 ${
        active ? 'bg-primary/10 text-primary font-medium' : 'text-fg-muted hover:bg-surface-muted'
      }`}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
